import React from 'react';
import { Layout } from 'antd';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router';

import { RootState } from 'app/redux';
import { NavigationService } from 'app/service/navigation-service';
import { Spinner } from 'app/components/common/spinner';
import { Summary } from 'app/page/orders/summary/summary';
import { NoInflightOrders } from 'app/page/orders/no-inflight-orders/no-inflight-orders';
import { RescheduleTechnicianPage } from 'app/page/orders/reschedule-technician/reschedule-technician-page';
import { Header } from 'app/components/domain/header/header';
import { Footer } from 'app/components/domain/footer/footer';
import { AuthCallbackPage } from 'app/page/auth/auth-callback-page';
import { FatalError } from 'app/components/domain/error/fatal-error';
import { AuthService } from 'app/service/auth-service';
import { FatalErrorBoundary } from 'app/components/domain/error/fatal-error-boundary';
import { Breadcrumb } from 'app/components/domain/breadcrumb/breadcrumb';
import { PendingAction } from 'app/page/orders/redux/model/pending-action';
import { PendingActionUtils } from 'app/page/orders/utils/pending-action-utils';
import { ProtectedRoute } from 'app/components/common/protected-route';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { BillingAccountUtils } from 'app/page/orders/utils/billing-account-utils';
import { UpdateActivationDatePage } from 'app/page/orders/update-activation-date/update-activation-date-page';
import { OrderUtils } from 'app/page/orders/utils/order-utils';
import { ChooseAccountPage } from 'app/page/orders/choose-account/choose-account-page';
import { ChooseServiceLocationPage } from 'app/page/orders/choose-service-location/choose-service-location-page';
import { FatalErrorType } from 'app/components/domain/error/fatal-error-type';
import { PendingType } from 'app/page/orders/redux/model/pending-type';
import { LogoutPage } from 'app/page/auth/logout-page';

import styles from './order-tracker-page.module.scss';

const { Content } = Layout;

interface StateProps {
    pendingActions: PendingAction[];
    billingAccounts: BillingAccount[];
    initialized: boolean;
    selectedBillingAccountId?: string;
}

type Props = StateProps & RouteComponentProps<void>;

class OrderTrackerPageComponent extends React.Component<Props> {
    public render(): React.ReactNode {
        const { pendingActions, initialized, selectedBillingAccountId, location } = this.props;

        if (AuthService.isAuthenticated() && PendingActionUtils.hasFailedActions(pendingActions)) {
            return (
                <Layout className={styles.pageLayout}>
                    <Header />
                    <Content>
                        <FatalError error={FatalErrorType.BACKEND_ERROR} />
                    </Content>
                    <Footer />
                </Layout>
            );
        }

        const showSpinner =
            (NavigationService.RESCHEDULE_TECHNICIAN !== location.pathname &&
                PendingActionUtils.hasPendingActions(pendingActions)) ||
            (NavigationService.RESCHEDULE_TECHNICIAN === location.pathname &&
                PendingActionUtils.isActionPending(pendingActions, PendingType.SUBMIT_SCHEDULE_APPOINTMENT)) ||
            (!AuthService.isAuthenticated() && location.pathname !== NavigationService.LOGOUT);

        return (
            <Spinner spinning={showSpinner}>
                <Layout className={styles.pageLayout}>
                    <Header />
                    <Content className={styles.content}>
                        <FatalErrorBoundary>
                            {this.showBreadcrumbs() && <Breadcrumb hasAppointment={!this.cannotChangeAppointment()} />}
                            <Switch>
                                <Route path={NavigationService.AUTH_CALLBACK} component={AuthCallbackPage} />
                                <Route path={NavigationService.LOGOUT} component={LogoutPage} />
                                <Route
                                    path={NavigationService.NO_BILLING_ACCOUNTS}
                                    component={() => FatalError({ error: FatalErrorType.NO_BILLING_ACCOUNTS })}
                                />

                                <ProtectedRoute
                                    path={NavigationService.CHOOSE_ACCOUNT}
                                    component={ChooseAccountPage}
                                    protections={[
                                        {
                                            isProtected: this.hasNoBillingAccounts,
                                            redirectTo: NavigationService.NO_BILLING_ACCOUNTS,
                                        },
                                        {
                                            isProtected: this.hasOnlyOneAccount,
                                            redirectTo: NavigationService.CHOOSE_SERVICE_LOCATION,
                                        },
                                    ]}
                                />
                                <ProtectedRoute
                                    path={NavigationService.CHOOSE_SERVICE_LOCATION}
                                    component={ChooseServiceLocationPage}
                                    protections={[
                                        {
                                            isProtected: () => !selectedBillingAccountId,
                                            redirectTo: NavigationService.CHOOSE_ACCOUNT,
                                        },
                                        {
                                            isProtected: this.hasOnlyOneOrder,
                                            redirectTo: NavigationService.SUMMARY,
                                        },
                                        {
                                            isProtected: this.shouldRedirectToNoInflightOrdersPage,
                                            redirectTo: NavigationService.NO_INFLIGHT_ORDERS,
                                        },
                                    ]}
                                />
                                <ProtectedRoute
                                    path={NavigationService.SUMMARY}
                                    component={Summary}
                                    protections={[
                                        {
                                            isProtected: () => !initialized || !selectedBillingAccountId,
                                            redirectTo: NavigationService.CHOOSE_ACCOUNT,
                                        },
                                        {
                                            isProtected: this.shouldRedirectToNoInflightOrdersPage,
                                            redirectTo: NavigationService.NO_INFLIGHT_ORDERS,
                                        },
                                        {
                                            isProtected: this.hasSelectedOrder,
                                            redirectTo: NavigationService.CHOOSE_SERVICE_LOCATION,
                                        },
                                    ]}
                                />
                                <ProtectedRoute
                                    protections={[
                                        {
                                            isProtected: () => !initialized,
                                            redirectTo: NavigationService.CHOOSE_ACCOUNT,
                                        },
                                    ]}
                                    path={NavigationService.NO_INFLIGHT_ORDERS}
                                    component={NoInflightOrders}
                                />
                                <ProtectedRoute
                                    protections={[
                                        {
                                            isProtected: this.cannotChangeAppointment,
                                            redirectTo: NavigationService.SUMMARY,
                                        },
                                    ]}
                                    path={NavigationService.RESCHEDULE_TECHNICIAN}
                                    component={RescheduleTechnicianPage}
                                />
                                <ProtectedRoute
                                    protections={[
                                        {
                                            isProtected: this.cannotChangeActivationDate,
                                            redirectTo: NavigationService.SUMMARY,
                                        },
                                    ]}
                                    path={NavigationService.UPDATE_ACTIVATION_DATE}
                                    component={UpdateActivationDatePage}
                                />
                                <Redirect to={NavigationService.CHOOSE_ACCOUNT} />
                            </Switch>
                        </FatalErrorBoundary>
                    </Content>
                    <Footer />
                </Layout>
            </Spinner>
        );
    }

    private readonly hasSelectedOrder = (): boolean => {
        const { billingAccounts, selectedBillingAccountId } = this.props;

        if (!selectedBillingAccountId) {
            return true;
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );
        const { selectedOrderId } = billingAccount;

        return !selectedOrderId;
    };

    private readonly hasOnlyOneOrder = (): boolean => {
        const { billingAccounts, selectedBillingAccountId } = this.props;

        if (!selectedBillingAccountId) {
            return false;
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );

        if (!billingAccount.initialized) {
            return false;
        }

        return Object.keys(billingAccount.activeOrders).length === 1;
    };

    private readonly hasOnlyOneAccount = (): boolean => {
        const { billingAccounts, initialized } = this.props;

        if (!initialized) {
            return false;
        }

        return billingAccounts.length === 1;
    };

    private readonly hasNoBillingAccounts = (): boolean => {
        const { billingAccounts, initialized } = this.props;

        return initialized && billingAccounts.length === 0;
    };

    private readonly shouldRedirectToNoInflightOrdersPage = (): boolean => {
        const { initialized, billingAccounts, selectedBillingAccountId } = this.props;

        if (!initialized || !selectedBillingAccountId) {
            return false;
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );

        if (!billingAccount.initialized) {
            return false;
        }

        return Object.keys(billingAccount.activeOrders).length === 0;
    };

    private readonly cannotChangeAppointment = (): boolean => {
        const { billingAccounts, selectedBillingAccountId } = this.props;

        if (!selectedBillingAccountId) {
            return true;
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );
        const { activeOrders, selectedOrderId } = billingAccount;

        if (!selectedOrderId) {
            return true;
        }

        return !OrderUtils.hasAppointment(activeOrders[selectedOrderId]);
    };

    private readonly cannotChangeActivationDate = (): boolean => {
        const { billingAccounts, selectedBillingAccountId } = this.props;

        if (!selectedBillingAccountId) {
            return true;
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );
        const { activeOrders, selectedOrderId } = billingAccount;

        if (!selectedOrderId) {
            return true;
        }

        const activeOrder = activeOrders[selectedOrderId];

        return OrderUtils.hasAppointment(activeOrder) || !OrderUtils.canChangeActivationDate(activeOrder);
    };

    private readonly showBreadcrumbs = (): boolean => {
        const { location, pendingActions } = this.props;

        return (
            !PendingActionUtils.isActionPending(pendingActions, PendingType.INITIALIZE_SCHEDULE_APPOINTMENT) &&
            [
                NavigationService.SUMMARY,
                NavigationService.RESCHEDULE_TECHNICIAN,
                NavigationService.UPDATE_ACTIVATION_DATE,
            ].includes(location.pathname)
        );
    };
}

const mapStateToProps = (state: RootState): StateProps => {
    const {
        orders: { pendingActions, billingAccounts, initialized, selectedBillingAccountId },
    } = state;

    return {
        pendingActions,
        billingAccounts,
        initialized,
        selectedBillingAccountId,
    };
};

const OrderTrackerPage = withRouter(connect(mapStateToProps)(OrderTrackerPageComponent));

export { OrderTrackerPage };
